import { useState } from 'react';
import { useT } from '~/i18n';
import H2StatusPicker from './H2StatusPicker';
import { ImmigrationSaveError, saveH2Status } from '../api/immigrationClient';
import { readH2Employer, readH2Status, type H2Status } from '../lib/immigration';
import type { JobPreferences } from '../lib/preferences';

/**
 * «Tu situación migratoria» en Perfil → Preferencias: la misma pregunta del
 * alta, para verla y cambiarla después (quien era returner y ahora está en EE.
 * UU. para una transferencia, por ejemplo).
 *
 * Guarda con su propio PATCH (sólo las dos claves H-2), como la tarjeta de
 * países: el formulario de las agencias de abajo no la toca. El botón aparece
 * sólo cuando algo cambió.
 */
interface Props {
  preferences?: JobPreferences | null;
}

type Notice = { kind: 'saved' } | { kind: 'error'; message: string } | null;

export default function ImmigrationStatusCard({ preferences }: Props) {
  const t = useT('onboarding').immigration;
  const [saved, setSaved] = useState(() => ({
    status: readH2Status(preferences),
    employer: readH2Employer(preferences),
  }));
  const [status, setStatus] = useState<H2Status | null>(saved.status);
  const [employer, setEmployer] = useState(saved.employer);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  const dirty = status !== saved.status || employer.trim() !== saved.employer.trim();

  const save = async () => {
    if (!status) return;
    setSaving(true);
    setNotice(null);
    try {
      const stored = await saveH2Status(status, employer);
      // Lo que el servidor dice que quedó manda; si no lo devuelve, lo enviado.
      const next = stored
        ? { status: readH2Status(stored) ?? status, employer: readH2Employer(stored) }
        : { status, employer: employer.trim() };
      setSaved(next);
      setStatus(next.status);
      setEmployer(next.employer);
      setNotice({ kind: 'saved' });
    } catch (err) {
      setNotice({
        kind: 'error',
        message: err instanceof ImmigrationSaveError ? err.message : t.saveFailed,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card" aria-labelledby="immigration-status-title">
      <div className="card-body py-4 space-y-3">
        <div className="space-y-1">
          <h2 id="immigration-status-title" className="font-semibold">{t.title}</h2>
          <p className="text-sm hush">{t.cardLede}</p>
        </div>

        <H2StatusPicker
          idPrefix="preferences"
          status={status}
          employer={employer}
          disabled={saving}
          onStatus={(next) => {
            setStatus(next);
            setNotice(null);
          }}
          onEmployer={(next) => {
            setEmployer(next);
            setNotice(null);
          }}
        />

        <p className="text-xs hush">{t.note}</p>

        {dirty && status && (
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving && <span className="loading loading-spinner loading-xs" />}
            {t.save}
          </button>
        )}

        {notice?.kind === 'saved' && (
          <p className="text-sm text-data" role="status">{t.saved}</p>
        )}
        {notice?.kind === 'error' && (
          <p className="text-sm" role="alert" style={{ color: 'var(--coral-ink)' }}>
            {notice.message}
          </p>
        )}
      </div>
    </section>
  );
}
